import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useMemo, Children } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { useTheme } from '@classify/react-core-components/styles';
import Box from '@classify/react-core-components/Box';
import ClassifyCarousel from '@classify/react-core-components/Carousel';
import Typography from '@classify/react-core-components/Typography';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import { styled } from '@classify/react-core-components/styles';
import type { Breakpoint } from './types/Breakpoint';
export const defaultTranslations = {
  previousLabel: 'Go back',
  nextLabel: 'Go to next slide'
};
export interface Props {
  title?: ReactNode;
  children?: ReactNode;
  sliderProps?: {
    previousArrowProps?: HTMLAttributes<HTMLButtonElement>;
    nextArrowProps?: HTMLAttributes<HTMLButtonElement>;
  };
  translations?: {
    previousLabel: string;
    nextLabel: string;
  };
  slidesPerPage?: number;
  slidesWidth?: Extract<Breakpoint, 'sm' | 'md' | 'lg'>;
  shouldHideNavigation?: boolean;
  shouldDisableCardFading?: boolean;
  onSlideChange?: (currentSlide: number) => void;
}
const CarouselHeader = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'flex',
  gap: spacing(2),
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  marginBottom: spacing(2),
  [breakpoints.up('md')]: {
    alignItems: 'center'
  }
}));
const CarouselNav = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  gap: spacing(2),
  alignItems: 'center'
}));
const CarouselArrow = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  width: 32,
  height: 32,
  borderRadius: '100%',
  border: '1px solid',
  cursor: 'pointer',
  '&:disabled': {
    cursor: 'auto',
    opacity: 0.5
  }
});
const CarouselContainer = styled('div', {
  shouldForwardProp: prop => !['slidesWidth', 'shouldDisableCardFading'].includes(prop as string)
})<{
  theme?: Theme;
  slidesWidth: Props['slidesWidth'];
  shouldDisableCardFading?: boolean;
}>(/* istanbul ignore next */
({
  theme: {
    spacing,
    breakpoints
  },
  slidesWidth,
  shouldDisableCardFading
}) => ({
  overflow: 'hidden',
  '&': {
    margin: spacing(0, -0.5),
    [breakpoints.up('md')]: {
      margin: spacing(0, -1)
    }
  },
  '& .slick-list': {
    overflow: 'visible',
    paddingBottom: spacing(2),
    width: '90%',
    ...(slidesWidth === 'sm' && {
      width: 280,
      [breakpoints.up('sm')]: {
        width: 520
      },
      [breakpoints.up('md')]: {
        width: 684
      }
    }),
    ...(slidesWidth === 'md' && {
      width: 400,
      [breakpoints.up('sm')]: {
        width: 720
      },
      [breakpoints.up('md')]: {
        width: 944
      }
    })
  },
  '& .slick-track': {
    display: 'flex'
  },
  '& .slick-slide': {
    transition: 'opacity 0.3s ease-out',
    opacity: shouldDisableCardFading ? 1 : 0.5,
    height: 'inherit !important',
    [breakpoints.up('md')]: {
      height: 'unset'
    }
  },
  '& .slick-slide.slick-active': {
    opacity: 1
  },
  '& .slick-slide > div': {
    height: '100%',
    padding: spacing(0, 0.5),
    [breakpoints.up('md')]: {
      padding: spacing(0, 1)
    }
  },
  '& .slick-slide > div > :first-of-type': {
    height: '100%'
  }
}));
const CardCarousel = t0 => {
  const $ = _c(42);
  const {
    title,
    children: t1,
    translations: t2,
    sliderProps: t3,
    slidesPerPage: t4,
    slidesWidth: t5,
    shouldHideNavigation,
    shouldDisableCardFading,
    onSlideChange
  } = t0;
  const children = t1 === undefined ? null : t1;
  let t6;
  if ($[0] !== t2) {
    t6 = t2 === undefined ? {
      previousLabel: defaultTranslations.previousLabel,
      nextLabel: defaultTranslations.nextLabel
    } : t2;
    $[0] = t2;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const translations = t6;
  let t7;
  if ($[2] !== t3) {
    t7 = t3 === undefined ? {} : t3;
    $[2] = t3;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const sliderProps = t7;
  const slidesPerPage = t4 === undefined ? 1 : t4;
  const slidesWidth = t5 === undefined ? "lg" : t5;
  const [currentPage, setCurrentPage] = useState(1);
  const cardCarouselSlider = useRef(null);
  const {
    breakpoints
  } = useTheme();
  let t8;
  if ($[4] !== breakpoints) {
    t8 = breakpoints.up("sm");
    $[4] = breakpoints;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const shouldDisplayArrows = useMediaQuery(t8);
  let t9;
  if ($[6] !== children) {
    t9 = Children.toArray(children);
    $[6] = children;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const cardCount = t9.length;
  const totalPages = Math.ceil(cardCount / slidesPerPage);
  const hasOnlyOneChild = cardCount === 1;
  if (!children) {
    return null;
  }
  let t10;
  if ($[8] !== currentPage || $[9] !== onSlideChange || $[10] !== totalPages) {
    t10 = (oldIndex, newIndex) => {
      const hasNextPage = currentPage < totalPages || newIndex < oldIndex;
      const hasPreviousPage = currentPage > 1 || newIndex > oldIndex;
      if (!hasNextPage || !hasPreviousPage) {
        return;
      }
      const currentPageIndex = newIndex >= oldIndex ? currentPage + 1 : currentPage - 1;
      setCurrentPage(currentPageIndex);
      onSlideChange?.(currentPageIndex);
    };
    $[8] = currentPage;
    $[9] = onSlideChange;
    $[10] = totalPages;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== slidesPerPage || $[13] !== t10) {
    t11 = {
      accessibility: true,
      infinite: false,
      slidesToShow: slidesPerPage,
      slidesToScroll: slidesPerPage,
      arrows: false,
      swipe: true,
      beforeChange: t10
    };
    $[12] = slidesPerPage;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const slickSettings = t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      cardCarouselSlider?.current?.slickPrev();
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handlePrevNav = t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      cardCarouselSlider?.current?.slickNext();
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const handleNextNav = t13;
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      overflow: "hidden"
    };
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      flexGrow: 1
    };
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== title) {
    t16 = <Box sx={t15}>{title}</Box>;
    $[19] = title;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== currentPage || $[22] !== hasOnlyOneChild || $[23] !== shouldDisplayArrows || $[24] !== shouldHideNavigation || $[25] !== sliderProps?.nextArrowProps || $[26] !== sliderProps?.previousArrowProps || $[27] !== totalPages || $[28] !== translations) {
    t17 = !hasOnlyOneChild && !shouldHideNavigation && <CarouselNav><Typography>{currentPage}/{totalPages}</Typography>{shouldDisplayArrows && <><CarouselArrow disabled={currentPage === 1} aria-label={translations.previousLabel} onClick={handlePrevNav} data-testid="card-carousel-previous" {...sliderProps?.previousArrowProps}><ChevronLeft /></CarouselArrow><CarouselArrow disabled={currentPage >= totalPages} aria-label={translations.nextLabel} onClick={handleNextNav} data-testid="card-carousel-next" {...sliderProps?.nextArrowProps}><ChevronRight /></CarouselArrow></>}</CarouselNav>;
    $[21] = currentPage;
    $[22] = hasOnlyOneChild;
    $[23] = shouldDisplayArrows;
    $[24] = shouldHideNavigation;
    $[25] = sliderProps?.nextArrowProps;
    $[26] = sliderProps?.previousArrowProps;
    $[27] = totalPages;
    $[28] = translations;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t16 || $[31] !== t17) {
    t18 = <CarouselHeader>{t16}{t17}</CarouselHeader>;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== children || $[34] !== hasOnlyOneChild || $[35] !== shouldDisableCardFading || $[36] !== slickSettings || $[37] !== slidesWidth) {
    t19 = hasOnlyOneChild ? children : <Box data-testid="card-carousel-item"><CarouselContainer slidesWidth={slidesWidth} shouldDisableCardFading={shouldDisableCardFading}><ClassifyCarousel {...slickSettings} ref={cardCarouselSlider}>{children}</ClassifyCarousel></CarouselContainer></Box>;
    $[33] = children;
    $[34] = hasOnlyOneChild;
    $[35] = shouldDisableCardFading;
    $[36] = slickSettings;
    $[37] = slidesWidth;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18 || $[40] !== t19) {
    t20 = <Box data-testid="card-carousel-wrapper-item" sx={t14}>{t18}{t19}</Box>;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
};
export default CardCarousel;